// Centralized portfolio content for Ali Renan Uyanık (sourced from CV).
export const profile = {
  name: "Ali Renan Uyanık",
  initials: "A.R.U.",
  role: "Electrical & Electronics Engineering Student",
  focus: "Robotics & Electronics",
  location: "Ataşehir, Istanbul",
  tagline:
    "Designing the bridge between silicon and motion: circuits that think, machines that move.",
  intro:
    "3rd-year Electrical & Electronics Engineering student. I lead robotics teams at TEKNOFEST and I'm currently building a quadruped robot to deepen my work at the intersection of robotics and AI.",
  email: "renanuya@gmail.com",
  website: "https://www.renanuya.com",
  languages: ["Turkish (Native)", "English (B2)"],
  socials: {
    github: "https://github.com/renanuya",
    linkedin: "https://www.linkedin.com/in/renanuya",
    instagram: "https://instagram.com/renanuya",
    x: "https://x.com/renanuya",
    discord: "https://discord.com/users/renanuya",
    website: "https://www.renanuya.com",
  },
};

export const marqueeSkills = [
  "ROS",
  "Python",
  "C++",
  "KiCad",
  "ESP32",
  "Jetson Nano",
  "Raspberry Pi",
  "LiDAR",
  "YOLOv8",
  "Isaac Sim",
  "Fusion 360",
  "Bullet",
  "IPC-2221",
  "Realsense",
  "Jira",
];

export const projects = [
  {
    id: "agv-teknofest",
    title: "Nova: Autonomous Guided Vehicle",
    year: "2024",
    tag: "TEKNOFEST · Team Captain",
    summary:
      "A 125 kg-payload autonomous guided vehicle built for TEKNOFEST's Industrial Digital Technologies competition, with a full autonomy stack from ROS navigation and SLAM mapping to a scissor-lift load handler.",
    contributions: [
      "Led the team as captain.",
      "Brought up and ran the full system on ROS.",
      "Owned the motion side with image processing and deep learning.",
      "Implemented the SLAM mapping and localization.",
    ],
    stack: ["ESP32", "Jetson Nano", "RPLIDAR", "ROS", "SLAM", "Python"],
    image: "/work/agv/cover.jpg",
    sections: [
      {
        text: "Nova is an autonomous guided vehicle (AGV) developed for TEKNOFEST's Industrial Digital Technologies competition by a six-person electrical-electronics engineering team. It follows a line-marked course, picks up and drops 125 kg loads autonomously, and maps its surroundings as it drives. The vehicle measures roughly 981 x 840 x 455 mm and runs about 55 minutes on a charge.",
        image: "/work/agv/01-vehicle.jpg",
        caption: "Final vehicle, roughly 981 x 840 x 455 mm",
      },
      {
        text: "Motion relies on a differential drive. Two braked NEMA 34 stepper-driven wheels handle propulsion and steering, while four casters spread the load and keep odometry clean. Force and friction calculations put the drivetrain's theoretical ceiling near 225 kg, comfortably above the 125 kg target.",
        image: "/work/agv/02-drivetrain.jpg",
        caption: "Differential drivetrain: braked NEMA 34 steppers and four casters",
      },
      {
        text: "Loads are raised by a scissor lift driven by 12 V linear actuators. The mechanism was modeled in Fusion 360 and sized with margin for a 150 kg case, well past the required payload.",
        image: "/work/agv/03-scissor-lift.jpg",
        caption: "Scissor lift driven by 12 V linear actuators",
      },
      {
        text: "The structure was validated in Fusion 360 with deformation, stress and safety-factor FEA under that 150 kg load, reaching a peak displacement of 0.03 mm and peak stress of 32.6 MPa. After iterating on materials, the chassis settled on aluminium sigma profile reinforced with steel profile.",
        image: "/work/agv/04-fea.jpg",
        caption: "Fusion 360 FEA under a 150 kg load",
      },
      {
        text: "Electronics are split across ESP32-S controllers that drive the motor stages (MA860 for the steppers, L298N for the actuator) and the sensor suite: a QTR-8RC line array, HC-SR04 ultrasonics, an HX711/L6G load cell, a DS18B20 temperature probe and a GY-271 compass. An NVIDIA Jetson Nano runs perception from an RPLIDAR A1M8 and a Logitech C925E camera, a 24 V 20 Ah pack feeds a custom power-distribution board, and the modules talk over UART.",
        image: "/work/agv/05-electronics.jpg",
        caption: "Electronics architecture",
      },
      {
        text: "Autonomy runs on ROS in Python: PID line following, obstacle avoidance, QR-triggered load pick-and-drop, and SLAM-based mapping and localization from the LIDAR point cloud. Every algorithm was validated in Gazebo before touching hardware.",
        image: "/work/agv/06-slam.jpg",
        caption: "SLAM mapping in Gazebo and RViz",
      },
      {
        text: "A Nextion control panel surfaces live speed, load, map, route and status, giving the operator a real-time view of the vehicle. Careful sourcing brought the build cost down from 56,507 to 53,422 Turkish lira while improving reliability.",
        image: "/work/agv/07-control-panel.jpg",
        caption: "Live control panel",
      },
    ],
  },
  {
    id: "agri-robot",
    title: "Laser Weeding Robot",
    year: "2025",
    tag: "TEKNOFEST · Team Captain",
    summary:
      "An agricultural robot that spots weeds with deep-learning vision and removes them with a targeted laser instead of chemicals, riding a Strider's Linkage walking mechanism for rough terrain.",
    contributions: [
      "Led the team as captain.",
      "Worked on part of the mechanical design.",
      "Took part in selecting the electronic components.",
      "Ran simulation tests in Isaac Sim.",
      "Built the weed image detection with YOLO.",
    ],
    stack: ["Deep Learning", "Computer Vision", "Python", "Strider's Linkage", "Laser", "3D Printing"],
    image: "/work/agri/01-robot.jpg",
    sections: [
      {
        text: "By 2050 the world's population is expected to reach about 9.6 billion, pushing global food demand up by roughly 70 percent. Weeds alone already cause crop losses of 32 to 34 percent worldwide, and the usual answer, chemical herbicides, degrades soil health, breeds resistant weeds and carries growing health concerns. Built by the Topkapi Dynamics team for TEKNOFEST's Agriculture Technologies competition, this robot offers a chemical-free alternative: it detects weeds early after planting and removes them with a focused laser.",
        image: "",
        caption: "",
      },
      {
        text: "A deep-learning vision model, trained on field imagery, identifies weeds in real time from the camera feed. Each detection is boxed with a confidence score, then handed to the laser module, which is aimed to remove the plant while keeping harm to the surrounding crop and soil to a minimum.",
        image: "/work/agri/02-detection.jpg",
        caption: "Real-time weed detection in the field",
      },
      {
        text: "Instead of wheels, the robot moves on a Strider's Linkage walking mechanism. That gives it better traction, maneuverability and energy efficiency on the uneven, soft ground of real farmland.",
        image: "/work/agri/03-strider-linkage.jpg",
        caption: "Strider's Linkage walking mechanism",
      },
      {
        text: "The chassis is a lightweight, modular ABS 3D print, with solar panels for energy efficiency and emergency stops for safety. By cutting chemical use to a minimum the system protects soil and water, stays cheap to source and easy to maintain, and is aimed at small and medium farms and organic growers.",
        image: "",
        caption: "",
      },
    ],
  },
  {
    id: "quadruped",
    title: "Quadruped Robot",
    year: "2025, In Progress",
    tag: "Personal · In Progress",
    summary:
      "A four-legged robot based on the MIT Mini Cheetah platform, built to serve as a physical body for large language and vision-language models. Mechanical design is almost complete.",
    body:
      "An ongoing personal project: a four-legged robot based on the MIT Mini Cheetah platform. The goal is to use it as a physical body for large language and vision-language models (LLM and VLM), so it can take high-level natural-language instructions and act on what it sees.\n\n" +
      "The mechanical design is almost finished. The next steps are fabrication, the actuation and control electronics, and a perception stack that feeds the vision-language model.",
    stack: ["Mini Cheetah", "LLM", "VLM", "Fusion 360", "ROS", "Custom PCB"],
    image: "/work/quad/cover.jpg",
  },
];

export const timeline = [
  {
    period: "Sep 2022 to 2027 (expected)",
    title: "B.Sc. Electrical & Electronics Engineering",
    org: "Istanbul Topkapı University",
    detail:
      "Third-year undergraduate focused on robotics, embedded systems, and the integration of AI into real-time hardware.",
  },
  {
    period: "Feb 2024 to Sep 2024",
    title: "Team Leader, Topkapı Dynamics",
    org: "TEKNOFEST",
    detail:
      "Captain and lead software engineer for a 6-7 person team delivering an AGV and an AMR. Built line tracking on ESP32, obstacle detection and avoidance with LiDAR and ultrasonic sensors, real-time OpenCV vision pipelines, and a web control panel for remote monitoring and task assignment. Validated navigation in NVIDIA Isaac Sim, on top of project management, budgeting and sponsor relations.",
  },
  {
    period: "Mar 2024 to Present",
    title: "Student Assistant",
    org: "Istanbul Topkapı University, EEE Dept.",
    detail:
      "Support laboratory courses: prepare equipment, supervise experiments, guide students, document procedures, and maintain lab safety.",
  },
  {
    period: "Nov 2025 to Present",
    title: "AI Engineer",
    org: "WYSEYE",
    detail:
      "Working on computer-vision and ML projects for real-time systems: model development, optimization, and system integration.",
  },
];

export const stats = [
  { label: "Year of B.Sc. EEE", value: "3rd" },
  { label: "TEKNOFEST projects led", value: "2" },
  { label: "Robots built", value: "03" },
  { label: "Quadruped in build", value: "01" },
];
